import * as React from "react";
import {cx} from "emotion";

import "./corporate-billing-accounts-base.scss";

import {cs}               from "@Common/react/chain-services";
import {fragments}        from "@Common/react/fragments";
import {Invoke}           from "@Common/react/invoke";
import {keyed}            from "@Common/react/keyed";
import {Static2}          from "@Common/react/static-2";
import {UseState}         from "@Common/react/use-state";
import {ErrorHighlighter} from "@Common/ui-components/light/error-highlighter";
import {scrollIntoView}   from "@Common/ui-components/light/scroll-into-view";
import {cascadeSum}       from "@Common/utils/cascade";
import {sum}              from "@Common/utils/collections";
import {isEmpty}          from "@Common/utils/objects";

import {BillingAccountForm} from "./billing-account-form/billing-account-form";
import {Header}             from "./header/header";

export const CorporateBillingAccountsBase = (
    {
        billingAccounts, appliedBillings,
        addBilling, removeBilling, updateBilling,
        next: rootNext
    }
) => cs(

    ["forms", ({}, next) => cs(
        ...billingAccounts
            ?.filter(({billingAccount}) => !isEmpty(billingAccount.fields))
            .map(({billingAccount}) => (
                [billingAccount.id, ({}, next) => BillingAccountForm({
                    avaiAccount: billingAccount,
                    getFieldSum: (fieldName) => sum(appliedBillings, (b) => +(b[fieldName] ?? 0)),
                    persistedBilling: appliedBillings?.find((b) => b.billingId == billingAccount.id),
                    next,
                })]
            )) ?? []
        ,
        next
    )],
    ["formDomRef", (_, next) => Static2({next})],
    ["adding", (_, next) => UseState({next})], // {billingId}
    ["calSelectedId", ({adding}, next) => next(
        adding.value ? (
            adding.value.billingId
        ) : (
            appliedBillings?.[0]?.billingId
        )
    )],
    ["activeForm", ({calSelectedId, forms}, next) => next(!calSelectedId ? null : forms[calSelectedId])],
    ({formDomRef, adding, activeForm, calSelectedId}) => {
        const invalid = activeForm?.hasError || adding.value || activeForm?.hasPendingChange;
        return rootNext({
            hasError: activeForm?.hasError,
            saving: activeForm?.saving,
            invalid,
            highlightErrors: invalid && (() => {
                formDomRef.get().highlightErrors();
            }),

            masterToggle: {
                value: !!calSelectedId,
                onChange: async () => {
                    if (!calSelectedId) {
                        const firstBillingAccount = billingAccounts[0].billingAccount;
                        const autoApply = isEmpty(firstBillingAccount.fields);
                        if (autoApply) {
                            await addBilling({billingId: firstBillingAccount.id});
                        } else {
                            adding.onChange({billingId: firstBillingAccount.id});
                        }
                    } else {
                        if (adding.value) {
                            adding.onChange(null);
                        } else {
                            await removeBilling();
                        }
                    }
                },
            },

            renderList: ({showErrors, apiErrors, asRadioButtons}) => (
                billingAccounts?.map(({billingAccount: account, amount}) => cs(
                    keyed(account.id),
                    ["appliedAmount", (_, next) => next(
                        cascadeSum(appliedBillings, "[*]", (b) => b.billingId === account.id && b.amount)
                    )],
                    ["errorHighlighter", (_, next) => ErrorHighlighter({next})],

                    ["autoApply", (_, next) => next(isEmpty(account.fields))],
                    ["selected", (_, next) => next(calSelectedId === account.id)],
                    ["form", ({selected}, next) => next(!selected ? null : activeForm)],
                    ["persistedBilling", ({selected}, next) => next(
                        !selected ? (
                            null
                        ) : (
                            appliedBillings?.[0]
                        )
                    )],
                    ({persistedBilling}, next) => fragments(
                        next(),
                        persistedBilling && adding.value && Invoke({
                            action: () => adding.onChange(null),
                        })
                    ),
                    ({appliedAmount, errorHighlighter, form, autoApply, selected, persistedBilling}) => (
                        <div
                            className={cx(
                                "corporate-billing-accounts-base-73j",
                                errorHighlighter.css,
                            )}
                            ref={(dom) => selected && formDomRef.set({
                                highlightErrors: () => {
                                    scrollIntoView(dom);
                                    (form?.hasError || apiErrors?.length) ? (
                                        errorHighlighter.start()
                                    ) : (
                                        form.highlightPendingChange()
                                    )
                                },
                            })}
                        >
                            {Header({
                                // disabled: forms.isAnySelected && !form.selected,
                                // disabled: calSelectedId != null && calSelectedId !== account.id,

                                hasError: showErrors && (
                                    form?.hasError || apiErrors?.length ||
                                    selected && (adding.value || form?.hasPendingChange)
                                ),

                                selected,
                                asRadioButtons,
                                onToggle: async () => {
                                    if (!selected) {
                                        if (autoApply) {
                                            await addBilling({billingId: account.id});
                                        } else {
                                            adding.onChange({billingId: account.id});
                                        }
                                    } else {
                                        if (adding.value) {
                                            adding.onChange(null);
                                        } else {
                                            await removeBilling();
                                        }
                                    }
                                },
                                account,
                                amount: appliedAmount ? appliedAmount : amount,
                            })}

                            {form && (
                                <div className="expansion">
                                    <div className="hr"/>

                                    {form.render({
                                        showErrors,
                                        apiErrors,
                                        pendingApply: adding.value,
                                        onApply: async ({fields}) => {
                                            if (adding.value) {
                                                return await addBilling({billingId: account.id, fields});
                                            } else {
                                                return await updateBilling({
                                                    billingId: account.id,
                                                    fields: {
                                                        ...persistedBilling,
                                                        ...fields,
                                                    },
                                                });
                                            }
                                        },
                                    })}
                                </div>
                            )}
                        </div>
                    ),
                ))
            ),
        });
    },
);

